import { BusDoorIcon, FridgeIcon, SeatIcon, ToiletIcon } from '@/assets/icons/bus/_';
import { BusEntityType } from '@/constants';
import React from 'react';
import { DeleteFilled } from '@ant-design/icons';
import { Button, Form, Input, Popover, Select, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { CreateUpdateSeatingModelDetailModel, CompanyTicketClassDto } from '@/__generated';
import { VALIDATE_REQUIRED } from '@/config';

type Props = {
  seat: CreateUpdateSeatingModelDetailModel;
  onRemove: (key: string) => void;
  onChange: (key: string, seatDetail: CreateUpdateSeatingModelDetailModel) => void;
  ticketClasses: CompanyTicketClassDto[];
};

export default function SeatDetail({ seat, onRemove, ticketClasses, onChange }: Props) {
  const { t } = useTranslation();
  const [showDetail, toggleDetail] = React.useState<boolean>(false);
  const [form] = Form.useForm();
  const ticketClassOptions = React.useMemo(() => {
    return ticketClasses.map(d => ({ value: d.ticketClassId, label: d.name }));
  }, [ticketClasses]);

  const color = React.useMemo(() => {
    return ticketClasses.find(x => x.ticketClassId === seat.baseTicketClassId)?.seatColor;
  }, [seat, ticketClasses]);
  const removeSeat = () => {
    onRemove(`${seat.row}-${seat.column}`);
  };

  if (seat.busEntityType === BusEntityType.Door) {
    return (
      <div className="seat-detail">
        <BusDoorIcon />
        <DeleteFilled className="delete-icon" onClick={removeSeat} />
      </div>
    );
  }
  if (seat.busEntityType === BusEntityType.Fridge) {
    return (
      <div className="seat-detail">
        <FridgeIcon />
        <DeleteFilled className="delete-icon" onClick={removeSeat} />
      </div>
    );
  }
  if (seat.busEntityType === BusEntityType.ToiletLeft) {
    return (
      <div className="seat-detail">
        <DeleteFilled className="delete-icon" onClick={removeSeat} />
        <ToiletIcon style={{ transform: 'rotateY(180deg)' }} />
      </div>
    );
  }
  if (seat.busEntityType === BusEntityType.ToiletRight) {
    return (
      <div className="seat-detail">
        <DeleteFilled className="delete-icon" onClick={removeSeat} />
        <ToiletIcon />
      </div>
    );
  }
  const onFinish = values => {
    toggleDetail(false);
    onChange(`${seat.row}-${seat.column}`, {
      ...seat,
      ...values,
    });
  };

  return (
    <div className="seat-detail">
      <Popover
        className="seat-container"
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <b>{seat.entityName || t('seatModelsPage_SeatInfo')}</b>
            <Button type="text" danger onClick={removeSeat}>
              <DeleteFilled />
            </Button>
          </div>
        }
        onOpenChange={v => {
          if (!v) {
            onFinish(form.getFieldsValue());
          }
          toggleDetail(v);
        }}
        trigger="click"
        open={showDetail}
        content={
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              entityName: seat.entityName,
              baseTicketClassId: seat.baseTicketClassId,
            }}
          >
            <Form.Item label={t('seatModelsPage_TicketClass')} name="baseTicketClassId">
              <Select options={ticketClassOptions}></Select>
            </Form.Item>
            <Form.Item label={t('seatModelsPage_SeatName')} name="entityName">
              <Input />
            </Form.Item>
          </Form>
        }
      >
        <SeatIcon style={{ fill: color || '#ccc', cursor: 'pointer', height: '70%' }} />
        <div className="seat-number">{seat.entityName}</div>
      </Popover>
    </div>
  );
}
